import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import type { TablesUpdate } from '../../types/database.types';

// The ThemeContext uses queryKey: ['branding', 'cime']
// We MUST use the same key here so invalidation triggers a re-fetch in ThemeProvider
const BRANDING_QUERY_KEY = ['branding', 'cime'] as const;

export function useUpdateBranding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TablesUpdate<'tenant_branding'>) => {
      const { data, error } = await supabase
        .from('tenant_branding')
        .update(input)
        .eq('tenant_slug', 'cime')
        .select()
        .single();
      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      // Invalidate ThemeContext's query — causes ThemeProvider to re-fetch and re-inject CSS vars
      queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    },
  });
}

export function useUploadLogo() {
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
      const path = `cime/logo-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('branding')
        .upload(path, file, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from('branding').getPublicUrl(path);
      return data.publicUrl;
    },
  });
}

// ── Theme presets ─────────────────────────────────────────────────────────────

export const THEME_PRESETS = {
  cime_classic: {
    theme_preset: 'cime_classic' as const,
    color_primary: '#043429',
    color_accent: '#718063',
    color_gold: '#c9a84c',
    color_bg: '#f6f4ee',
    color_surface: '#ffffff',
    sidebar_style: 'dark',
    color_sidebar_bg: null,
    color_sidebar_text: null,
    color_sidebar_text_muted: null,
    color_sidebar_active_bg: null,
    color_sidebar_border: null,
    color_text_primary: '#1f2823',
    color_text_muted: '#5c6660',
    color_danger: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706',
    font_heading: 'Playfair Display',
    font_body: 'Inter',
    border_radius: '8px',
  },
  midnight: {
    theme_preset: 'midnight' as const,
    color_primary: '#1e293b',
    color_accent: '#64748b',
    color_gold: '#fbbf24',
    color_bg: '#0f172a',
    color_surface: '#1e293b',
    sidebar_style: 'dark',
    color_sidebar_bg: '#0f172a',
    color_sidebar_text: '#e2e8f0',
    color_sidebar_text_muted: '#94a3b8',
    color_sidebar_active_bg: 'rgba(255,255,255,0.08)',
    color_sidebar_border: 'rgba(255,255,255,0.08)',
    color_text_primary: '#e2e8f0',
    color_text_muted: '#94a3b8',
    color_danger: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706',
    font_heading: 'Playfair Display',
    font_body: 'Inter',
    border_radius: '6px',
  },
  sunset: {
    theme_preset: 'sunset' as const,
    color_primary: '#b91c1c',
    color_accent: '#ea580c',
    color_gold: '#eab308',
    color_bg: '#fef2f2',
    color_surface: '#ffffff',
    sidebar_style: 'tinted',
    color_sidebar_bg: null,
    color_sidebar_text: null,
    color_sidebar_text_muted: null,
    color_sidebar_active_bg: null,
    color_sidebar_border: null,
    color_text_primary: '#1a1a1a',
    color_text_muted: '#78716c',
    color_danger: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706',
    font_heading: 'Playfair Display',
    font_body: 'Inter',
    border_radius: '8px',
  },
  forest: {
    theme_preset: 'forest' as const,
    color_primary: '#14532d',
    color_accent: '#65a30d',
    color_gold: '#ca8a04',
    color_bg: '#f7faf5',
    color_surface: '#ffffff',
    sidebar_style: 'tinted',
    color_sidebar_bg: null,
    color_sidebar_text: null,
    color_sidebar_text_muted: null,
    color_sidebar_active_bg: null,
    color_sidebar_border: null,
    color_text_primary: '#1a1a1a',
    color_text_muted: '#6b7280',
    color_danger: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706',
    font_heading: 'Merriweather',
    font_heading_url: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap',
    font_body: 'Inter',
    font_body_url: null,
    border_radius: '10px',
  },
  marine: {
    theme_preset: 'marine' as const,
    color_primary: '#1e3a8a',
    color_accent: '#0ea5e9',
    color_gold: '#d4af37',
    color_bg: '#f8fafc',
    color_surface: '#ffffff',
    sidebar_style: 'light',
    color_sidebar_bg: null,
    color_sidebar_text: null,
    color_sidebar_text_muted: null,
    color_sidebar_active_bg: null,
    color_sidebar_border: null,
    color_text_primary: '#1a1a1a',
    color_text_muted: '#6b7280',
    color_danger: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706',
    font_heading: 'Lora',
    font_heading_url: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;600;700&display=swap',
    font_body: 'Inter',
    font_body_url: null,
    border_radius: '8px',
  },
  bordeaux: {
    theme_preset: 'bordeaux' as const,
    color_primary: '#4a1d2e',
    color_accent: '#8b5a2b',
    color_gold: '#b8860b',
    color_bg: '#faf7f4',
    color_surface: '#ffffff',
    sidebar_style: 'dark',
    color_sidebar_bg: '#4a1d2e',
    color_sidebar_text: '#f5f0e8',
    color_sidebar_text_muted: 'rgba(245,240,232,0.65)',
    color_sidebar_active_bg: 'rgba(255,255,255,0.12)',
    color_sidebar_border: 'rgba(255,255,255,0.08)',
    color_text_primary: '#1a1a1a',
    color_text_muted: '#78716c',
    color_danger: '#dc2626',
    color_success: '#16a34a',
    color_warning: '#d97706',
    font_heading: 'Cormorant Garamond',
    font_heading_url: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&display=swap',
    font_body: 'Lato',
    font_body_url: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap',
    border_radius: '4px',
  },
} as const;

export type ThemePresetKey = keyof typeof THEME_PRESETS;

export function useApplyThemePreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preset: ThemePresetKey) => {
      const values = THEME_PRESETS[preset];
      const { data, error } = await supabase
        .from('tenant_branding')
        .update(values)
        .eq('tenant_slug', 'cime')
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    },
  });
}
